// ─────────────────────────────────────────────────────────────────────────────
// 🔑 EDIT APPLICATION (Jesse, 2026-10-16). One card, two states, both from the server
// (GET /api/applications/edit-state):
//   editable — "Edit your application": reopens steps 1–3 with the saved answers. Every change is saved and
//              recorded as the borrower goes; step 3's "Save changes" returns them to where they belong.
//   locked   — after the step-8 decision: "Request a change" sends a note to the team (a staff task).
// Renders nothing for a borrower not on the guided flow, or one who has not submitted yet.
//
// Props: token; onEdit (optional — inside the shell, its goToStep(1)); compact (a one-line link in the shell).
// ─────────────────────────────────────────────────────────────────────────────
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_API_URL || '';

export default function EditApplicationCard({ token, onEdit, compact = false }) {
  const navigate = useNavigate();
  const [st, setSt] = useState(null);
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null);
  const [error, setError] = useState(null);
  const headers = token ? { Authorization: `Bearer ${token}` } : undefined;

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/api/applications/edit-state`, { headers })
      .then((r) => { if (!cancelled) setSt(r.data || null); })
      .catch(() => { if (!cancelled) setSt(null); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (!st || !st.submitted) return null;

  const startEdit = async () => {
    setError(null);
    if (typeof onEdit === 'function') { onEdit(1); return; }
    setBusy(true);
    try {
      await axios.put(`${API}/api/guided-flow/state`, { step: 1 }, { headers });
      navigate('/apply/guided');
    } catch (_) {
      setError('We could not open your application just now. Please try again.');
    } finally { setBusy(false); }
  };

  const sendRequest = async () => {
    setError(null);
    if (note.trim().length < 5) { setError('Tell us what you would like to change.'); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/applications/change-request`, { message: note.trim() }, { headers });
      setSent(r.data && r.data.ticketNumber ? r.data.ticketNumber : 'sent');
      setOpen(false);
      setNote('');
    } catch (e) {
      setError((e && e.response && e.response.data && e.response.data.message) || 'We could not send that just now. Please try again.');
    } finally { setBusy(false); }
  };

  const box = compact
    ? { marginTop: 12, fontSize: 13, color: '#000' }
    : { border: '1px solid #e5e7eb', borderRadius: 12, padding: 16, background: '#fff', marginBottom: 16 };
  const btn = { padding: '8px 14px', fontSize: 13, fontWeight: 600, borderRadius: 8, cursor: busy ? 'wait' : 'pointer',
    border: '1px solid #d1d5db', background: '#fff', color: '#000' };

  if (st.editable) {
    if (compact) {
      return (
        <div data-testid="edit-application-card" data-state="editable" style={box}>
          Need to change an answer?{' '}
          <button type="button" data-testid="edit-application-open" onClick={startEdit} disabled={busy}
            style={{ background: 'none', border: 0, padding: 0, color: '#000', textDecoration: 'underline', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>
            Edit your application
          </button>
          {error && <div style={{ color: '#b91c1c', fontSize: 12, marginTop: 4 }}>{error}</div>}
        </div>
      );
    }
    return (
      <div data-testid="edit-application-card" data-state="editable" style={box}>
        <div style={{ fontSize: 15, fontWeight: 700, color: '#000', marginBottom: 4 }}>Your application</div>
        <p style={{ fontSize: 13, color: '#000', margin: '0 0 12px', lineHeight: 1.5 }}>
          You can change your answers until we make a decision. Your changes are saved as you go.
          {st.pulled ? ' Because your credit has already been checked, your changes are checked against it. We will not check your credit again.' : ''}
        </p>
        <button type="button" data-testid="edit-application-open" onClick={startEdit} disabled={busy} style={btn}>Edit your application</button>
        {error && <div style={{ color: '#b91c1c', fontSize: 12, marginTop: 8 }}>{error}</div>}
      </div>
    );
  }

  if (!st.locked) return null;
  // 🚨 NOT ONCE AN OFFER IS REQUESTED (the Home Shopping page redesign, 2026-11-02): the server refuses a change request
  // from offer_requested onward (backend utils/applicationEdits changeRequestOpen), so the card is not offered either.
  if (st.changeRequestOpen === false) return null;
  return (
    <div data-testid="edit-application-card" data-state="locked" style={box}>
      {!compact && <div style={{ fontSize: 15, fontWeight: 700, color: '#000', marginBottom: 4 }}>Your application</div>}
      {/* 🔑 APPROVED COPY (Hannah's review, Jesse 2026-11-02 item 9); the button stays "Request a change". */}
      <p data-testid="edit-application-locked-copy" style={{ fontSize: 13, color: '#000', margin: '0 0 12px', lineHeight: 1.5 }}>
        Your application is decided, so it can&rsquo;t be edited. If something has changed, let us know.
      </p>
      {sent ? (
        <div data-testid="edit-application-request-sent" style={{ fontSize: 13, color: '#065f46', background: '#ecfdf5',
          border: '1px solid #a7f3d0', borderRadius: 8, padding: '8px 12px' }}>
          Thanks — we have your request{sent !== 'sent' ? ` (reference ${sent})` : ''}.
        </div>
      ) : open ? (
        <div>
          <label htmlFor="edit-application-note" style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: '#000', marginBottom: 4 }}>
            What would you like to change?
          </label>
          <textarea id="edit-application-note" data-testid="edit-application-note" value={note} onChange={(e) => setNote(e.target.value)}
            rows={3} maxLength={1000}
            style={{ width: '100%', boxSizing: 'border-box', fontSize: 13, padding: 8, borderRadius: 8, border: '1px solid #d1d5db' }} />
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button type="button" data-testid="edit-application-send" onClick={sendRequest} disabled={busy}
              style={{ ...btn, background: '#3a6ad9', color: '#fff', borderColor: '#3a6ad9' }}>Send request</button>
            <button type="button" onClick={() => { setOpen(false); setError(null); }} disabled={busy} style={btn}>Cancel</button>
          </div>
        </div>
      ) : (
        <button type="button" data-testid="edit-application-request" onClick={() => setOpen(true)} style={btn}>Request a change</button>
      )}
      {error && <div style={{ color: '#b91c1c', fontSize: 12, marginTop: 8 }}>{error}</div>}
    </div>
  );
}
